import moment from "moment/moment";
import _ from "lodash";
import { TFunction } from "i18next";
import { EXCEL_DATE_FORMAT, EXCEL_DATE_TIME_FORMAT, ExportColumnDef, toExcelDate } from "../../utils/listExport";

/**
 * Shared definition of the additional "Semicon export" event columns.
 *
 * These columns are hidden by default in the global event table and surface in
 * the list export. Driving both the table cells and the export from this single
 * list keeps them from drifting apart.
 *
 * Most values come from the event's `attributeList` (keyed by `property.alias`),
 * the rest from the asset, asset location and installed-base node path. The raw
 * `data` JSON is intentionally not used here.
 */

export type ExtraEventColumn = ExportColumnDef;

// Aliases whose attribute values are dates / datetimes and need formatting.
const DATE_ALIASES = new Set(["removalDate", "installationDate", "warrantyEndDate"]);
const DATE_TIME_ALIASES = new Set(["callDate", "startDate", "finishDate"]);

const parseDate = (value: any): moment.Moment | null => {
    if (value === null || value === undefined || value === "") return null;
    // Mongo-style extended JSON sometimes leaks through as { $date: { $numberLong } }
    if (typeof value === "object" && value.$date?.$numberLong) {
        const m = moment(Number(value.$date.$numberLong));
        return m.isValid() ? m : null;
    }
    const m = moment(value);
    return m.isValid() ? m : null;
};

const findAttribute = (row: any, alias: string): any =>
    _.get(row, "attributeList.attributes", [])?.find(
        (a: any) => a?.property?.alias === alias
    );

/** Raw machine value for date parsing (value preferred — ISO/epoch — over the
 *  possibly already-localized displayValue). */
const getRawAttributeDateValue = (row: any, alias: string): any => {
    const attr = findAttribute(row, alias);
    if (!attr) return null;
    return attr.value ?? attr.displayValue ?? null;
};

/** Formatted attribute value as a display string (dates formatted via moment). */
export const getEventAttributeValue = (row: any, alias: string): string => {
    const attr = findAttribute(row, alias);
    if (!attr) return "";

    if (DATE_ALIASES.has(alias) || DATE_TIME_ALIASES.has(alias)) {
        const m = parseDate(attr.value ?? attr.displayValue);
        if (m) return m.format(DATE_TIME_ALIASES.has(alias) ? "L LT" : "L");
    }

    const raw = attr.displayValue ?? attr.value;
    if (raw === null || raw === undefined || raw === "") return "";
    if (Array.isArray(raw)) return raw.join(", ");
    if (typeof raw === "boolean") return raw ? "Yes" : "No";
    return String(raw);
};

/** Attribute date as Excel cell value; null when unset or not a date. */
const getEventAttributeDate = (row: any, alias: string): Date | null => {
    const m = parseDate(getRawAttributeDateValue(row, alias));
    return m ? toExcelDate(m) : null;
};

/** Raw attribute value as number (e.g. running time hours); null when not numeric. */
const getEventAttributeNumber = (row: any, alias: string): number | null => {
    const value = findAttribute(row, alias)?.value;
    if (value === null || value === undefined || value === "" || typeof value === "boolean") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/** Title of the path node with the given node-type key (e.g. SEMICONDUCTOR_FAB). */
export const getNodeTitleByType = (row: any, nodeTypeKey: string): string => {
    const node = _.get(row, "installedBaseNode.pathNodes", [])?.find(
        (n: any) => n?.nodeType?.key === nodeTypeKey
    );
    return node?.title ?? "";
};

/**
 * Warranty status at the time of the removal event: compares the removal/event
 * date against the warranty end date. Empty when no warranty end date is known.
 */
export const getWarrantyStatus = (row: any, t: TFunction): string => {
    const warrantyEnd =
        parseDate(getRawAttributeDateValue(row, "warrantyEndDate")) ??
        parseDate(_.get(row, "assetLocation.warrantyEndDate"));
    if (!warrantyEnd) return "";

    const eventDate =
        parseDate(getRawAttributeDateValue(row, "removalDate")) ??
        parseDate(_.get(row, "insert"));
    if (!eventDate) return "";

    return eventDate.isAfter(warrantyEnd)
        ? t("outside of warranty")
        : t("within warranty");
};

export const getExtraEventColumns = (t: TFunction): ExtraEventColumn[] => {
    const attr = (alias: string) => (row: any) => getEventAttributeValue(row, alias);
    // Text as in the table, plus a real date for the Excel export
    const dateAttr = (alias: string) => ({
        getValue: attr(alias),
        getTypedValue: (row: any) => getEventAttributeDate(row, alias),
        excelFormat: DATE_TIME_ALIASES.has(alias) ? EXCEL_DATE_TIME_FORMAT : EXCEL_DATE_FORMAT,
    });

    return [
        { field: "eventId", headerName: t("Event ID"), getValue: (row) => String(row?.id ?? "") },
        { field: "removalDate", headerName: t("Removal Date"), ...dateAttr("removalDate") },
        {
            field: "serialNumber",
            headerName: t("Serial No."),
            getValue: (row) => row?.asset?.serialNumber ?? "",
        },
        {
            field: "productCode",
            headerName: t("Pump Model / Product Code"),
            getValue: (row) => {
                const product = row?.asset?.product;
                if (!product) return "";
                return product.productCode || product.name || "";
            },
        },
        {
            field: "customer",
            headerName: t("Customer"),
            getValue: (row) => _.get(row, "assetLocation.owner.name", "") || "",
        },
        {
            field: "fab",
            headerName: t("Fab"),
            getValue: (row) => getNodeTitleByType(row, "SEMICONDUCTOR_FAB"),
        },
        {
            field: "chamberId",
            headerName: t("Chamber ID"),
            getValue: (row) => getNodeTitleByType(row, "SEMICONDUCTOR_CHAMBER"),
        },
        { field: "installationDate", headerName: t("Installation Date"), ...dateAttr("installationDate") },
        {
            field: "runningTimeHours",
            headerName: t("Running Time"),
            getValue: attr("runningTimeHours"),
            getTypedValue: (row) => getEventAttributeNumber(row, "runningTimeHours"),
        },
        {
            field: "warrantyEndDate",
            headerName: t("Warranty End Date"),
            getValue: (row) => {
                const fromAttr = getEventAttributeValue(row, "warrantyEndDate");
                if (fromAttr) return fromAttr;
                const m = parseDate(_.get(row, "assetLocation.warrantyEndDate"));
                return m ? m.format("L") : "";
            },
            getTypedValue: (row) => {
                const fromAttr = getEventAttributeDate(row, "warrantyEndDate");
                if (fromAttr) return fromAttr;
                const m = parseDate(_.get(row, "assetLocation.warrantyEndDate"));
                return m ? toExcelDate(m) : null;
            },
            excelFormat: EXCEL_DATE_FORMAT,
        },
        {
            field: "warrantyStatus",
            headerName: t("Warranty Status at Removal"),
            getValue: (row) => getWarrantyStatus(row, t),
        },
        { field: "typeOfWarranty", headerName: t("Type of Warranty"), getValue: attr("typeOfWarranty") },
        { field: "maintenanceType", headerName: t("Maintenance Type"), getValue: attr("maintenanceType") },
        { field: "removalReason", headerName: t("Removal Reason"), getValue: attr("removalReason") },
        { field: "reasonCode", headerName: t("Reason Code"), getValue: attr("reasonCode") },
        {
            field: "waferScrapInformation",
            headerName: t("Wafer Scrap Information"),
            getValue: attr("waferScrapInformation"),
        },
        { field: "callDate", headerName: t("Call Date"), ...dateAttr("callDate") },
        { field: "startDate", headerName: t("Start Date"), ...dateAttr("startDate") },
        { field: "finishDate", headerName: t("Finish Date"), ...dateAttr("finishDate") },
    ];
};

export const EXTRA_EVENT_COLUMN_FIELDS: string[] = [
    "eventId",
    "removalDate",
    "serialNumber",
    "productCode",
    "customer",
    "fab",
    "chamberId",
    "installationDate",
    "runningTimeHours",
    "warrantyEndDate",
    "warrantyStatus",
    "typeOfWarranty",
    "maintenanceType",
    "removalReason",
    "reasonCode",
    "waferScrapInformation",
    "callDate",
    "startDate",
    "finishDate",
];

/** Default column-visibility model that hides every extra column. */
export const EXTRA_EVENT_DEFAULT_VISIBILITY: Record<string, boolean> = Object.fromEntries(
    EXTRA_EVENT_COLUMN_FIELDS.map((f) => [f, false])
);
